import React, { useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { BillingItem } from '../hooks/useBillingStatus';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    items: BillingItem[];
    billingInfo?: string;
    isProcessing?: boolean;
    onRefresh?: () => Promise<void>;
}

const MODAL_POLL_INTERVAL_MS = 8000;

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (date: string) => date ? date.split('-').reverse().join('/') : '-';

const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
        REGISTERED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        ERROR: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        PROCESSING: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

const BillingDetailModal: React.FC<Props> = ({ isOpen, onClose, items, billingInfo, isProcessing, onRefresh }) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isOpen && isProcessing && onRefresh) {
            intervalRef.current = setInterval(onRefresh, MODAL_POLL_INTERVAL_MS);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isOpen, isProcessing, onRefresh]);

    if (!isOpen) return null;

    const totalAmount = items.reduce((sum, i) => sum + i.amount, 0);
    const errorCount = items.filter(i => i.error_message).length;
    const registeredCount = items.filter(i => i.status === 'REGISTERED').length;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-5xl mx-4 flex flex-col max-h-[85vh]">
                <div className="flex-shrink-0 flex justify-between items-center p-5 border-b border-gray-200 dark:border-slate-600">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Detalhes do Faturamento</h2>
                        {billingInfo && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{billingInfo}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                        {isProcessing && (
                            <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Atualizando...
                            </span>
                        )}
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <FaTimes size={18} />
                        </button>
                    </div>
                </div>

                <div className="flex-shrink-0 px-5 py-3 bg-gray-50 dark:bg-slate-700 flex gap-6 text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Total: <strong>{items.length}</strong> título(s)</span>
                    <span className="text-gray-600 dark:text-gray-300">Valor: <strong>{formatCurrency(totalAmount)}</strong></span>
                    <span className="text-green-600 dark:text-green-400">Registrados: <strong>{registeredCount}</strong></span>
                    {errorCount > 0 && (
                        <span className="text-red-600 dark:text-red-400">Com erro: <strong>{errorCount}</strong></span>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="py-2 px-3">#</th>
                                <th className="py-2 px-3">Valor</th>
                                <th className="py-2 px-3">Vencimento</th>
                                <th className="py-2 px-3">Emissão</th>
                                <th className="py-2 px-3">Status</th>
                                <th className="py-2 px-3">Contrato</th>
                                <th className="py-2 px-3">Pagador</th>
                                <th className="py-2 px-3">Erro</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr
                                    key={item.charge_id || idx}
                                    className={`border-b border-gray-100 dark:border-slate-700 ${item.error_message ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                                >
                                    <td className="py-2 px-3">{item.installment_number}</td>
                                    <td className="py-2 px-3 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                                    <td className="py-2 px-3 whitespace-nowrap">{formatDate(item.due_date)}</td>
                                    <td className="py-2 px-3 whitespace-nowrap">{formatDate(item.emission_date)}</td>
                                    <td className="py-2 px-3">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(item.status)}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3">{item.contract_number || '-'}</td>
                                    <td className="py-2 px-3 max-w-[180px]">
                                        <div className="truncate" title={item.payer?.name}>{item.payer?.name || '-'}</div>
                                        <div className="text-xs text-gray-400">{item.payer?.document_number || ''}</div>
                                    </td>
                                    <td className="py-2 px-3 max-w-[200px] text-red-600 dark:text-red-400 text-xs">
                                        {item.error_message || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex-shrink-0 flex justify-end p-4 border-t border-gray-200 dark:border-slate-600">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-slate-500">
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BillingDetailModal;
